import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

// ─── Types ──────────────────────────────────────────────────────────────────────

export type LayoutMode = 'auto' | 'desktop' | 'compact';
export type FontSize = 'small' | 'normal' | 'large';
export type SplitPreset = 'up' | 'middle' | 'down';

export const SPLIT_PRESETS: Record<SplitPreset, number> = {
  up: 15,     // Banner + tabs only (~15% of height)
  middle: 33, // 1/3 list, 2/3 panel
  down: 85,   // ~85% list, minimal panel
};

const FONT_SCALES: Record<FontSize, number> = {
  small: 0.85,
  normal: 1.0,
  large: 1.15,
};

export interface LayoutContextValue {
  isCompact: boolean;
  mode: LayoutMode;
  fontSize: FontSize;
  splitRatio: number;
  setSplitRatio: (ratio: number | SplitPreset) => void;
  setMode: (mode: LayoutMode) => void;
  setFontSize: (size: FontSize) => void;
}

// ─── Constants ──────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'moby-layout-prefs';
const COMPACT_BREAKPOINT = 768; // px — below this, auto mode picks compact

interface StoredPreferences {
  mode: LayoutMode;
  fontSize: FontSize;
  splitRatio: number;
}

const DEFAULT_PREFS: StoredPreferences = {
  mode: 'auto',
  fontSize: 'normal',
  splitRatio: SPLIT_PRESETS.middle,
};

// ─── Context ────────────────────────────────────────────────────────────────────

export const LayoutContext = createContext<LayoutContextValue>({
  isCompact: false,
  mode: 'auto',
  fontSize: 'normal',
  splitRatio: SPLIT_PRESETS.middle,
  setSplitRatio: () => {},
  setMode: () => {},
  setFontSize: () => {},
});

export function useLayoutPreferences(): LayoutContextValue {
  return useContext(LayoutContext);
}

// ─── Provider hook (used by MobyChatPageLayout) ─────────────────────────────────

export function useLayoutPreferencesProvider(): LayoutContextValue {
  const [prefs, setPrefs] = useState<StoredPreferences>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return { ...DEFAULT_PREFS, ...parsed };
      }
    } catch { /* ignore corrupt data */ }
    return DEFAULT_PREFS;
  });

  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );

  // Track viewport width for auto-detect
  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Persist to localStorage on change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch { /* storage full or unavailable */ }
  }, [prefs]);

  // Apply font scaling as CSS custom property on :root
  useEffect(() => {
    const scale = FONT_SCALES[prefs.fontSize] ?? 1;
    document.documentElement.style.setProperty('--layout-font-scale', String(scale));
  }, [prefs.fontSize]);

  // Keyboard push-up: visualViewport fallback for browsers that don't support
  // interactive-widget=resizes-content (the meta tag handles modern Chrome/Brave)
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    const handleResize = () => {
      const offset = window.innerHeight - vv.height;
      document.documentElement.style.setProperty(
        '--keyboard-inset-height',
        `${Math.max(0, offset)}px`
      );
    };

    vv.addEventListener('resize', handleResize);
    return () => vv.removeEventListener('resize', handleResize);
  }, []);

  // Determine effective compact mode
  const isCompact = useMemo(() => {
    if (prefs.mode === 'desktop') return false;
    if (prefs.mode === 'compact') return true;
    // Auto: compact below breakpoint
    return viewportWidth < COMPACT_BREAKPOINT;
  }, [prefs.mode, viewportWidth]);

  const setSplitRatio = useCallback((ratio: number | SplitPreset) => {
    const numeric = typeof ratio === 'string' ? SPLIT_PRESETS[ratio] : ratio;
    setPrefs(prev => ({ ...prev, splitRatio: Math.max(10, Math.min(90, numeric)) }));
  }, []);

  const setMode = useCallback((mode: LayoutMode) => {
    setPrefs(prev => ({ ...prev, mode }));
  }, []);

  const setFontSize = useCallback((fontSize: FontSize) => {
    setPrefs(prev => ({ ...prev, fontSize }));
  }, []);

  return useMemo(() => ({
    isCompact,
    mode: prefs.mode,
    fontSize: prefs.fontSize,
    splitRatio: prefs.splitRatio,
    setSplitRatio,
    setMode,
    setFontSize,
  }), [isCompact, prefs.mode, prefs.fontSize, prefs.splitRatio, setSplitRatio, setMode, setFontSize]);
}
